import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { Search, Plus, Users, Building, MapPin, Calendar, Filter, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format, parseISO } from 'date-fns';
import RecruitingProjectForm from '@/components/dashboard/recruiting-project-form';

export default function ProjectsPage() {
  const { user, currentMode, hasLicense } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [formOpen, setFormOpen] = useState(false);
  const [userCompanies, setUserCompanies] = useState<any[]>([]);

  useEffect(() => {
    if (!user) return;
    
    // Check for hire license
    if (currentMode !== 'hire' || !hasLicense('hire')) {
      navigate('/pricing');
      return;
    }
    
    fetchProjects();
  }, [user, currentMode, hasLicense, navigate]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      // Fetch projects directly by profile_id
      const { data, error } = await supabase
        .from('recruiting_projects')
        .select(`
          *,
          profiles (
            id,
            full_name,
            company_name
          ),
          applications (id, status)
        `)
        .eq('profile_id', user?.id)
        .order('created_at', { ascending: false });
        
      if (error) throw error;
      
      // Process and set projects
      processProjects(data || []);
      
      // Also fetch user companies for the form
      fetchUserCompanies();
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({ 
        variant: 'destructive', 
        title: 'Error', 
        description: 'Failed to load projects' 
      });
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };
  
  const fetchUserCompanies = async () => {
    try {
      // First check for company_users associations
      const { data: companyUsers, error: companyUsersError } = await supabase
        .from('company_users')
        .select('company_id, companies(id, name)')
        .eq('user_id', user?.id)
        .eq('is_active', true);

      if (companyUsersError) throw companyUsersError;
      
      // Also check for primary_company_id in profiles
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('primary_company_id, company_name')
        .eq('id', user?.id)
        .single();
        
      if (profileError && profileError.code !== 'PGRST116') throw profileError;
      
      // Combine both sources of company information
      const companies = [];
      
      // Add companies from company_users
      if (companyUsers && companyUsers.length > 0) {
        companyUsers.forEach(cu => {
          if (cu.companies) {
            companies.push({
              id: cu.company_id,
              name: cu.companies.name
            });
          }
        });
      }
      
      // Add primary company from profile if not already included
      if (profile?.primary_company_id && !companies.some(c => c.id === profile.primary_company_id)) {
        companies.push({
          id: profile.primary_company_id,
          name: profile.company_name || 'My Company'
        });
      }
      
      setUserCompanies(companies);
    } catch (error) {
      console.error('Error fetching user companies:', error);
    }
  };
  
  // Helper function to process projects and calculate progress
  const processProjects = (projectsData: any[]) => {
    const projectsWithProgress = projectsData.map(project => {
      const counts = { ai: 0, staff: 0, client: 0, applied: 0 };
      const stageCounts: Record<string, number> = {};

      project.applications?.forEach((app: any) => {
        switch (app.source) {
          case 'ai_presented': counts.ai++; break;
          case 'staff_sourced': counts.staff++; break;
          case 'client_sourced': counts.client++; break;
          case 'applied': counts.applied++; break;
        }
      });

      const totalApplications = project.applications?.length || 0;
      const progress = Math.round((totalApplications / 10) * 100); // Simple progress calculation

      return { ...project, counts, stageCounts, progress };
    });

    setProjects(projectsWithProgress);
  };

  // Get unique departments for filtering
  const departments = [...new Set(projects.map(project => project.department))].filter(Boolean);

  // Filter and sort projects
  const filteredProjects = projects.filter(project => {
    if (statusFilter !== 'all' && project.is_active !== (statusFilter === 'active')) {
      return false;
    }
    
    if (departmentFilter !== 'all' && project.department !== departmentFilter) {
      return false;
    }
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        project.title?.toLowerCase().includes(query) ||
        project.department?.toLowerCase().includes(query) ||
        project.location?.toLowerCase().includes(query) ||
        project.profiles?.company_name?.toLowerCase().includes(query)
      );
    }
    
    return true;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'title':
        return a.title.localeCompare(b.title);
      case 'applications':
        return (b.applications?.length || 0) - (a.applications?.length || 0);
      case 'progress':
        return (b.progress || 0) - (a.progress || 0);
      default:
        return 0;
    }
  });

  if (currentMode !== 'hire' || !hasLicense('hire')) {
    return (
      <div className="container py-8 px-4 md:px-6">
        <Card>
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You need a hire license to access this feature.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/pricing')}>Upgrade Now</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  return (
    <div className="container py-8 px-4 md:px-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Recruiting Projects</h1>
            <p className="text-muted-foreground">
              Manage your recruiting projects and track applicants
            </p>
          </div>
          <Button onClick={() => setFormOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                placeholder="Search projects..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date Created</SelectItem>
                <SelectItem value="title">Title</SelectItem>
                <SelectItem value="applications">Applications</SelectItem>
                <SelectItem value="progress">Progress</SelectItem>
              </SelectContent>
            </Select>

            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => {
                setSearchQuery('');
                setStatusFilter('all');
                setDepartmentFilter('all');
                setSortBy('date');
              }}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Projects List */}
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : filteredProjects.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <div className="text-center">
                <Building className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No Projects Found</h3>
                <p className="text-muted-foreground mb-4">
                  {searchQuery || statusFilter !== 'all' || departmentFilter !== 'all'
                    ? 'Try adjusting your filters'
                    : 'Create your first recruiting project to get started'}
                </p>
                <Button onClick={() => setFormOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className={project.is_active ? 'border-primary/20' : ''}>
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div className="space-y-1">
                      <CardTitle className="line-clamp-1">{project.title}</CardTitle>
                      <CardDescription>{project.department}</CardDescription>
                    </div>
                    <Badge variant={project.is_active ? 'default' : 'secondary'}>
                      {project.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Progress Bar */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-muted-foreground" />
                          <span>Application Progress</span>
                        </div>
                        <span className="font-medium">{project.progress}%</span>
                      </div>
                      <Progress value={project.progress} className="h-2" />
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>
                          {project.applications?.length || 0} applicants
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1 text-xs text-muted-foreground">
                      <span>AI: {project.counts.ai}</span>
                      <span>Staff: {project.counts.staff}</span>
                      <span>Client: {project.counts.client}</span>
                      <span>Applied: {project.counts.applied}</span>
                    </div>

                    <div className="flex flex-wrap gap-2 text-sm">
                      <div className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{project.location}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{formatDate(project.created_at)}</span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {project.applications?.length || 0} applicants
                        </span>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => navigate(`/dashboard/applications/${project.id}`)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <RecruitingProjectForm 
        open={formOpen} 
        onOpenChange={setFormOpen} 
        onSuccess={fetchProjects}
        userCompanies={userCompanies}
      />
    </div>
  );
}